import api from './api';
import { useToolUsageStore } from '../store/toolUsageStore';

// Backend response envelope is { success, message, data }; the axios response
// interceptor already unwraps to that object, so we read `.data` for the payload.
const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

const toolsService = {
    // ── Favorites (server-backed, per user) ──────────────────────────────────
    /**
     * List the authenticated user's favorited tool slugs.
     * @returns {Promise<Array<{ toolSlug: string, createdAt: string }>>}
     */
    getFavorites: async () => {
        const res = await api.get('/api/tool-favorites');
        return unwrap(res) || [];
    },

    /**
     * Toggle a tool favorite.
     * @param {string} slug e.g. "pdf/compress"
     * @returns {Promise<{ toolSlug: string, isFavorite: boolean }>}
     */
    toggleFavorite: async (slug) => {
        const res = await api.post('/api/tool-favorites/toggle', { slug });
        return unwrap(res);
    },

    // ── Usage tracking ───────────────────────────────────────────────────────
    /**
     * Record a single tool-usage event. Call this from a tool's main submit /
     * action handler (the moment the tool actually runs). Fire-and-forget.
     * @param {string} slug e.g. "pdf/compress"
     * @param {{ category?: string, officeSpaceId?: number, metadata?: object }} [opts]
     */
    recordUsage: async (slug, opts = {}) => {
        const res = await api.post('/api/tool-usage', { slug, ...opts });
        return unwrap(res);
    },

    /** Recently used tools for the current user. */
    getRecent: async (limit = 8) => {
        const res = await api.get('/api/tool-usage/recent', { params: { limit } });
        return unwrap(res) || [];
    },

    /** Most used tools for the current user. */
    getMostUsed: async (limit = 8) => {
        const res = await api.get('/api/tool-usage/most-used', { params: { limit } });
        return unwrap(res) || [];
    },

    /** Most used tools across ALL users, last 30 days: [{ toolSlug, count }] — drives the "Hot" badge. */
    getPopular: async () => {
        const res = await api.get('/api/tool-usage/popular');
        return unwrap(res) || [];
    },

    /** ALL-TIME runs per tool across every user: [{ toolSlug, count }] — the tool page's "used N times". */
    getTotals: async () => {
        const res = await api.get('/api/tool-usage/totals');
        return unwrap(res) || [];
    },

    /** Usage summary { totalUses, distinctTools }. */
    getStats: async () => {
        const res = await api.get('/api/tool-usage/stats');
        return unwrap(res) || { totalUses: 0, distinctTools: 0 };
    },
};

export default toolsService;

/**
 * Convenience helper for tool pages: record a usage event without caring about
 * the result or errors. Drop this into a tool's submit handler.
 *
 *   import { recordToolUsage } from '@/services/toolsService';
 *   const onCompress = async () => { recordToolUsage('pdf/compress', { category: 'pdf' }); ...run tool... };
 *
 * @param {string} slug
 * @param {{ category?: string, officeSpaceId?: number, metadata?: object }} [opts]
 */
export function recordToolUsage(slug, opts = {}) {
    try {
        useToolUsageStore.getState().bump(slug);
        toolsService.recordUsage(slug, opts).catch(() => { /* non-blocking */ });
    } catch { /* never throw from a usage ping */ }
}
